import { useCallback, useEffect, useMemo } from "react";
import { useReducer } from "react";
import { useAuthProvider, useNotify } from "react-admin";
import {
  ADD_REPORT,
  initialReportContext,
  IReport,
  ReportContext,
  reportContextReducer,
} from "./context";
import { ReportContextAccessor } from "./ReportContextAccessor";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";

interface Props {
  children: React.ReactNode;
}

export const ReportClient = ({ children }: Props) => {
  const [state, dispatch] = useReducer(
    reportContextReducer,
    initialReportContext
  );
  const notify = useNotify();
  const contextValue = useMemo(
    () => new ReportContextAccessor(dispatch, state, notify),
    [state, notify]
  );

  const handleReportReceived = useCallback(
    (report: IReport) => {
      if (report.success) {
        notify("messages.reportGenerated", { type: "success" });
      } else {
        notify("messages.reportFailed", { type: "error" });
      }

      dispatch({ type: ADD_REPORT, payload: report });
    },
    [notify]
  );

  const authProvider = useAuthProvider();
  useEffect(() => {
    let hubConnection: HubConnection | undefined;
    authProvider.checkAuth(null).then(() => {
      const apiUrl =
        process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
        "https://localhost:44327";

      hubConnection = new HubConnectionBuilder()
        .withUrl(`${apiUrl}/signalr-hubs/report-hub`, {
          accessTokenFactory: authProvider.getAccessToken,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      hubConnection.on("receiveReport", handleReportReceived);
      hubConnection.start();
    });

    return () => {
      if (hubConnection) {
        hubConnection.off("receiveReport");
        hubConnection.stop();
      }
    };
  }, [authProvider, handleReportReceived]);

  return (
    <ReportContext.Provider value={contextValue}>
      {children}
    </ReportContext.Provider>
  );
};
